import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

// Contrato: endpoints `clube/*` do sistemaadm (ClubeResource). O header `estabelecimento`
// (loja em uso) é enviado pelo interceptor de ./api; aqui só vai o Authorization.

/** CUPOM: cupons por ciclo. FRETE_GRATIS: entrega grátis a partir de valorMinimoPedido (sem cupom). */
export type TipoBeneficioClube = "CUPOM" | "FRETE_GRATIS"

export interface BeneficioClube {
    id?: number | null
    tipo: TipoBeneficioClube
    quantidade: number
    valor: number
    percentual: boolean
    valorMinimoPedido: number | null
    maximoDesconto: number | null
    ativo: boolean
    ordem: number
}

export interface ClubeBeneficiosApi {
    id: number | null
    ativo: boolean
    nome: string
    descricao: string
    valorMensal: number
    /** posição do plano na lista (menor primeiro) */
    ordem: number
    beneficios: BeneficioClube[]
}

// Contrato v2: a loja pode ter VÁRIOS clubes (planos). O cliente só tem um plano por vez.
export interface ObterClubeResposta {
    /** ordenados por ordem, depois valorMensal */
    clubes: ClubeBeneficiosApi[]
    /** = clubes[0] (compatibilidade com o contrato v1) */
    clube: ClubeBeneficiosApi | null
    recebedorConfigurado: boolean
}

export interface SalvarClubeItemPayload {
    id?: number
    ativo: boolean
    nome: string
    descricao: string
    valorMensal: number
    ordem: number
    beneficios: BeneficioClube[]
    deletarBeneficioIds: number[]
}

export interface SalvarClubePayload {
    clubes: SalvarClubeItemPayload[]
    /** planos salvos a excluir (o backend recusa se o plano já teve assinaturas) */
    deletarClubeIds: number[]
}

export type StatusAssinaturaClube = "PENDENTE" | "ATIVA" | "INADIMPLENTE" | "CANCELADA"

export interface AssinaturaClube {
    id: number
    status: StatusAssinaturaClube
    valorMensal: number
    criadaEm: number | null
    canceladaEm: number | null
    cicloAtualInicio: number | null
    cicloAtualFim: number | null
    cartaoFinal: string | null
    cartaoBandeira: string | null
    /** plano (clube) da assinatura */
    clubeId: number
    clubeNome: string
}

export interface AssinanteClube extends AssinaturaClube {
    clienteId: number
    clienteNome: string | null
    telefone: string | null
    cuponsUsados: number
    cuponsTotal: number
}

export interface TotaisAssinantesClube {
    pendentes: number
    ativas: number
    inadimplentes: number
    canceladas: number
}

/** Soma das mensalidades pagas no mês corrente da loja (valores em reais). */
export interface ResumoFinanceiroClube {
    mes: string // "MM/AAAA"
    bruto: number
    taxa: number
    liquido: number
    mensalidades: number
    /** mensalidades cuja taxa/líquido ainda está em processamento (contam só no bruto) */
    pendentes?: number
}

export interface ObterAssinantesResposta {
    assinantes: AssinanteClube[]
    totais: TotaisAssinantesClube
    resumoFinanceiro?: ResumoFinanceiroClube | null
}

/** Uma mensalidade paga. `taxa`/`valorLiquido` null = ainda em processamento. */
export interface MensalidadeClube {
    id: number
    inicio: number | null
    fim: number | null
    pagoEm: number | null
    valorBruto: number | null
    taxa: number | null
    valorLiquido: number | null
}

export interface ObterMensalidadesResposta {
    mensalidades: MensalidadeClube[]
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

export function isErroApi(res: unknown): res is ErroApi {
    return typeof res === "object" && res !== null && "retornoErro" in res
}

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data && data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if ((typeof data === "string" && data !== "") || data === null || data === undefined) {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

function headers(authorization: string) {
    return { "Content-Type": "application/json", Authorization: authorization }
}

export const obterClubeEstabelecimento = async function (
    authorization: string
): Promise<ObterClubeResposta | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ObterClubeResposta>>("clube/ObterClubeEstabelecimento", {
                headers: headers(authorization),
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ObterClubeResposta>(response, "ObterClubeEstabelecimento")
    } catch (error) {
        RegistrarLogErro(error, "ObterClubeEstabelecimento", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarClubeEstabelecimento = async function (
    authorization: string,
    payload: SalvarClubePayload
): Promise<ObterClubeResposta | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ObterClubeResposta>>("clube/SalvarClubeEstabelecimento", payload, {
                headers: headers(authorization),
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ObterClubeResposta>(response, "SalvarClubeEstabelecimento")
    } catch (error) {
        RegistrarLogErro(error, "SalvarClubeEstabelecimento", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const obterAssinantesClube = async function (
    authorization: string,
    status?: StatusAssinaturaClube | "",
    clubeId?: number | ""
): Promise<ObterAssinantesResposta | ErroApi> {
    const params: { status?: StatusAssinaturaClube; clubeId?: number } = {}
    if (status) params.status = status
    if (clubeId) params.clubeId = clubeId
    try {
        const response = await api
            .get<RespostaRaw<ObterAssinantesResposta>>("clube/ObterAssinantes", {
                headers: headers(authorization),
                params,
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ObterAssinantesResposta>(response, "ObterAssinantes")
    } catch (error) {
        RegistrarLogErro(error, "ObterAssinantes", JSON.stringify(params))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const obterMensalidadesAssinatura = async function (
    authorization: string,
    idAssinatura: number
): Promise<ObterMensalidadesResposta | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ObterMensalidadesResposta>>(
                `clube/ObterMensalidadesAssinatura/${idAssinatura}`,
                { headers: headers(authorization) }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ObterMensalidadesResposta>(
            response,
            "ObterMensalidadesAssinatura"
        )
    } catch (error) {
        RegistrarLogErro(error, "ObterMensalidadesAssinatura", String(idAssinatura))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const cancelarAssinaturaClube = async function (
    authorization: string,
    idAssinatura: number,
    motivo?: string
): Promise<{ assinatura: AssinaturaClube } | ErroApi> {
    const body = motivo && motivo.trim() ? { motivo: motivo.trim() } : {}
    try {
        const response = await api
            .post<RespostaRaw<{ assinatura: AssinaturaClube }>>(
                `clube/CancelarAssinaturaPainel/${idAssinatura}`,
                body,
                { headers: headers(authorization) }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ assinatura: AssinaturaClube }>(
            response,
            "CancelarAssinaturaPainel"
        )
    } catch (error) {
        RegistrarLogErro(
            error,
            "CancelarAssinaturaPainel",
            JSON.stringify({ idAssinatura, ...body })
        )
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
